import { useI18n } from '@/hooks/useI18n';
import { useRedirectAfterReserve } from '@/hooks/useRedirectAfterReserve';
import useReserveResources from '@/hooks/useReserveResources';
import { useOfficeRestrictions } from '@/hooks/useRestrictions';
import { canScheduleOthers } from '@/lib/permissions';
import {
  convertTZ,
  getMinDateAllowedToReserve,
  getTimeForReservation
} from '@/lib/utils';
import {
  getDurationLabel,
  getDurationOptions,
  getOfficeHealthReservationRestrictions,
  noParkingResource
} from '@/lib/utils-reserve';
import {
  type SaveReservationData,
  saveReservations
} from '@/lib/utils-reserve';
import { isValidEmail } from '@/lib/validation/email';
import type { IResource } from '@gettactic/api';
import { useFloors } from '@gettactic/hooks';
import { useAuthenticated } from '@gettactic/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { PropsWithChildren, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
  ReserveFormDefaultValues,
  ReserveFormState,
  ReserveProviderProps
} from '@/components/reserve/Reserve/types';
import { useBooleanState } from '@gettactic/helpers';
import { getDefaultsHoursFromOffice } from '@gettactic/helpers';

export const ReserveFormContext = React.createContext<ReserveFormState | null>(
  null
);

export const ReserveFormProvider: React.FC<
  PropsWithChildren<ReserveProviderProps> & {
    onRoomChange?: (roomId: string | null) => void;
    hasMSCalendar?: boolean;
    hasGoogleCalendar?: boolean;
  }
> = ({ children, ...reserveProps }) => {
  const {
    floorId,
    resourceType: _resourceType,
    start,
    resource,
    time,
    duration,
    onSuccess,
    onRoomChange,
    hasMSCalendar,
    hasGoogleCalendar
  } = reserveProps;
  const resourceType = _resourceType || 'workspace';
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);
  const { setAfterReserve } = useRedirectAfterReserve();

  const [selectedOffice, setSelectedOffice] = React.useState(
    reserveProps.office ?? currentOffice
  );
  const [filterFloor, setFilterFloor] = React.useState<string | null>(
    floorId ?? authenticatedUser.defaultAreaId
  );
  const isLoading = useBooleanState();
  const [error, setError] = React.useState('');

  const [enableAdminReservation, setEnableAdminReservation] =
    React.useState(false);

  const queryClient = useQueryClient();
  const { floors } = useFloors(selectedOffice?.id ?? '');

  // We should validate that the floor filter selected
  // exists among the current floor list
  React.useEffect(() => {
    if (!floors || !filterFloor) {
      return;
    }
    if (!floors.find((x) => x.id === filterFloor)) {
      setFilterFloor(null);
    }
  }, [floors, filterFloor]);

  // Resource Types
  const isMeetingRoom = resourceType === 'meeting_room';
  const isDesk = resourceType === 'workspace' || resourceType === 'desk';
  const isParking =
    resourceType === 'parking_space' || resourceType === 'parking_lot';
  const allowOthers = canScheduleOthers(authenticatedUser);

  // If we aren't given a start date, use the current date/time in the selected office's timezone
  const startOfficeDate: Date =
    start || convertTZ(new Date(), selectedOffice.time_zone);
  const offices = authenticatedUser.offices?.offices ?? [];
  const { meetingRoomDuration, deskDuration } =
    getDefaultsHoursFromOffice(selectedOffice);

  // Default or last accessed values
  const lastAccessedResourceId = isMeetingRoom
    ? authenticatedUser?.user?.organization_user
        .last_accessed_meeting_room_resource_id || null
    : authenticatedUser?.user?.organization_user
        .last_accessed_desk_resource_id || null;
  const lastAccessedParkingResourceId =
    authenticatedUser?.user?.organization_user
      .last_accessed_parking_resource_id || noParkingResource.id;
  const currentOfficeTz = selectedOffice.time_zone;
  const currentOfficeId = selectedOffice.id;
  const currentOfficeDayIndex = convertTZ(new Date(), currentOfficeTz).getDay();

  // Track if duration was custom selected from props or changed by user
  const userChangedDuration = React.useRef(false);
  const userStartTimeChanged = React.useRef(false);

  // For meeting rooms, always use office defaults
  // For other types, use duration prop if provided, otherwise use office default
  // Those rules were set with previous logic
  const initialDuration = isMeetingRoom
    ? meetingRoomDuration * 60
    : duration
      ? duration
      : deskDuration * 60;

  // If duration is specified in props and different from default, 
  // mark as user-changed, so it doesn't get reset on office change
  React.useEffect(() => {
    const isDurationPropDifferent = duration && !isMeetingRoom && duration !== deskDuration * 60;
    const isTimePropDifferent = start && start !== startOfficeDate;
    if (isDurationPropDifferent || isTimePropDifferent) {
      userChangedDuration.current = true;
      userStartTimeChanged.current = true;
    }
  }, []);

  const defaultValues: ReserveFormDefaultValues = {
    title: '',
    resource_id: resource?.id ?? lastAccessedResourceId,
    parking_resource_id: lastAccessedParkingResourceId,
    start: startOfficeDate,
    time: getTimeForReservation(startOfficeDate, time, currentOffice),
    duration: initialDuration,
    description: ''
  };

  if (!defaultValues.start || !(defaultValues.start instanceof Date)) {
    defaultValues.start = getMinDateAllowedToReserve(selectedOffice.time_zone);
  }

  const form = useForm<SaveReservationData>({
    defaultValues: {
      title: defaultValues.title,
      weekDates: [defaultValues.start],
      description: defaultValues.description,
      online_meeting: null,
      customMeetingLink: ''
    }
  });

  // Watching form values due to field dependencies
  const weekDates = form.watch('weekDates');
  const users = (form.watch('users') ?? []).map((x) => x.value);
  const organizer = form.watch('organizer');
  const currentDuration = form.watch('duration', {
    value: defaultValues.duration
  });

  const weekDatesStr = weekDates.map((weekDate: Date) =>
    format(weekDate, 'yyyy-MM-dd')
  );
  const [durationMins, setDurationMins] = React.useState<number>(
    () => defaultValues.duration
  );
  const [startTime, setStartTime] = React.useState<Date>(
    () => defaultValues.time
  );

  const prevOfficeId = React.useRef(selectedOffice.id);

  const handleDurationChange = React.useCallback(
    (newDuration: number | React.SetStateAction<number>) => {
      userChangedDuration.current = true;
      setDurationMins(newDuration);
    },
    [durationMins]
  );

  const handleStartTimeChange = React.useCallback(
    (newStartTime: Date | React.SetStateAction<Date>) => {
      userStartTimeChanged.current = true;
      setStartTime(newStartTime);
    },
    [startTime]
  );

  React.useEffect(() => {
    const { meetingRoomDuration, deskDuration } =
      getDefaultsHoursFromOffice(selectedOffice);
    const officeDate = convertTZ(new Date(), selectedOffice.time_zone);
    const newDefaultTime = getTimeForReservation(
      officeDate,
      time,
      selectedOffice
    );

    
    const isOfficeChanged = prevOfficeId.current !== selectedOffice.id;
    // Only update office ID after checking if it changed
    prevOfficeId.current = selectedOffice.id;

    const isTimeChangedByUser = userStartTimeChanged.current;

    // Update start time if it hasn't been changed or office changed
    if (!isTimeChangedByUser && isOfficeChanged) {
      form.setValue('time', newDefaultTime);
      setStartTime(newDefaultTime);
    }

    // Only update duration if:
    // 1. It's a meeting room (always use office defaults) OR
    // 2. Office changed AND user hasn't manually changed duration
    const isDurationChangedByUser = userChangedDuration.current;
    if (isMeetingRoom || (isOfficeChanged && !isDurationChangedByUser && !isTimeChangedByUser)) {
      const newDuration = isMeetingRoom
        ? meetingRoomDuration * 60
        : deskDuration * 60;
      setDurationMins(newDuration);
      form.setValue('duration', { value: newDuration });
    }
  }, [selectedOffice, isMeetingRoom, form]);

  // When reserving on behalf of another user, use _that_ person's resource permissions & assignments
  const userIdForResources = enableAdminReservation
    ? isMeetingRoom
      ? (organizer?.value ?? null)
      : users[0]
    : null;
  const { resourcesList, office, parkingResourcesList } = useReserveResources({
    resourceType,
    officeId: currentOfficeId,
    durationMins,
    startTime,
    weekDatesStr,
    filterParent: filterFloor,
    organizer: isValidEmail(userIdForResources) ? null : userIdForResources
  });
  const { restrictions } = useOfficeRestrictions(
    currentOfficeId,
    startTime,
    weekDatesStr,
    durationMins,
    []
  );

  // If we pass a resource to the component, we show that resource EVEN if it is not available eg in the list
  // This allows schedulers/admins to choose a resource from the map, even when they don't have permission to reserve it themselves.
  // It also facilitates meeting room reservations that are not perfectly aligned with the default map filter times.
  const { updatedResourcesList, defaultResource } = React.useMemo(() => {
    let updatedResourcesList = resourcesList;
    let defaultResource: IResource | null =
      defaultValues.resource_id && resourcesList
        ? (resourcesList.find(
            (resource) => resource.id === defaultValues.resource_id
          ) ?? null)
        : null;
    if (resource && resourcesList) {
      defaultResource = resource;
      // If the default resource is not available, add it to the list so it can be selected. Omit if it is already in the list.
      if (!resourcesList.find((x) => x.id === resource.id)) {
        updatedResourcesList = [...resourcesList, resource];
      }
    }
    return {
      updatedResourcesList,
      defaultResource
    };
  }, [resourcesList, resource, defaultValues]);

  const durationOptions = React.useMemo(
    () => getDurationOptions(startTime),
    [startTime, currentOfficeTz]
  );
  const currentResource = form.watch('resource', defaultResource ?? undefined);
  useEffect(() => {
    onRoomChange?.(resourceType === 'meeting_room' ? currentResource?.id : null);
  }, [currentResource?.id, resourceType, onRoomChange]);

  const isUserHealthRestricted = !enableAdminReservation
    ? getOfficeHealthReservationRestrictions(
        authenticatedUser,
        office ?? undefined
      )
    : false;

  const submitMutation = useMutation(
    async (data: SaveReservationData) => {
      const authUserId = authenticatedUser?.user?.id;
      // If the user is not allowed to schedule in the past & the time selected is more than 5 minutes in the past, throw an error message
      // if (
      //   isNoPastReservations(authenticatedUser) &&
      //   isPast(subMinutes(data.time, 5))
      // ) {
      //   setError(
      //     'Reservations in the past are disabled. Please choose a time in the future.'
      //   );
      //   throw new Error();
      // }
      
      if (!office || !authUserId) {
        // just to avoid ts warning
        throw new Error('Not required params');
      }
      isLoading.true();

      if (!data.duration?.value || data.duration.value < 5) {
        form.setError('duration', {
          type: 'manual',
          message: 'Duration must be at least 5 minutes'
        });
        throw new Error();
      }
      
      const { hasError, message } = await saveReservations(data, {
        organizerId: data.organizer?.value ?? null,
        isMeetingRoom,
        authUserId,
        office,
        // allowOthers && the admin has chosen a user
        allowOthers: enableAdminReservation,
        timeZone: office.time_zone,
        officeId: currentOfficeId,
        noParkingResource,
        dateFormat: templates.displayDate,
        orgSlug: authenticatedUser.slug || ''
        
      });

      if (hasError && message) {
        setError(message);
        throw new Error();
      }
    },
    {
      onError: () => {
        isLoading.false();
      },
      onSuccess: () => {
        // This should return false for 95% of users currently.
        // TODO: Investigate if there is a less expensive way to determine if we should call this.
        setAfterReserve(selectedOffice.id);
        onSuccess?.();
      },
      onSettled: () => {
        queryClient.invalidateQueries(['schedules']);
        queryClient.invalidateQueries({ queryKey: ['myAgenda'], exact: false });
        queryClient.invalidateQueries(['organizations.schedulable']);
        queryClient.invalidateQueries(['tableOverview']);
        queryClient.invalidateQueries(['organizations.questionnaire']);
        isLoading.false();
      }
    }
  );

  const [reservationDurationLabel, setReservationDurationLabel] = React.useState(getDurationLabel(currentDuration.value));
  React.useEffect(() => {
    setReservationDurationLabel(getDurationLabel(currentDuration.value));
  }, [currentDuration.value]);

  const submitReservation: SubmitHandler<SaveReservationData> = async (
    data
  ) => {
    submitMutation.mutate(data);
  };

  const totalParking =
    (office?.total_parking_lots ?? 0) + (office?.total_parking_spaces ?? 0);

  const allowParking = isDesk && !isParking && !!office && totalParking > 0;

  const isSubmitDisabled =
    isLoading.state ||
    isUserHealthRestricted ||
    !updatedResourcesList ||
    !currentResource;

  return (
    <ReserveFormContext.Provider
      value={{
        ...reserveProps,
        resourceType,
        form,
        office,
        isMeetingRoom,
        weekDates,
        currentOfficeTz,
        startTime,
        defaultValues,
        currentOfficeId,
        durationOptions,
        defaultResource,
        durationMins,
        updatedResourcesList,
        parkingResourcesList,
        allowParking,
        floors,
        filterFloor,
        selectedOffice,
        currentOffice,
        offices,
        users,
        organizer,
        errorMessage: error,
        restrictions: restrictions?.restrictions || [],
        enableAdminReservation,
        allowOthers,
        isLoading: isLoading.state,
        isUserHealthRestricted,
        isSubmitDisabled,
        reservationDurationLabel,
        currentResource,
        hasMSCalendar,
        hasGoogleCalendar,
        // Actions
        setStartTime: handleStartTimeChange, // Use the new handler that tracks user changes
        setDurationMins: handleDurationChange, // Use the new handler that tracks user changes
        setFilterFloor,
        setSelectedOffice,
        setEnableAdminReservation,
        submitReservation
      }}
    >
      {children}
    </ReserveFormContext.Provider>
  );
};

export const useReserveForm = () => {
  const context = React.useContext(ReserveFormContext);
  if (!context) {
    throw new Error('useReserveForm must be used within a ReserveFormProvider');
  }
  return context;
};
